<template>
    <div v-if="usageType === UpgradeUsageType.TRY_PREMIUM" class="package-diff-edition over-limit">
        <div class="relative flex justify-between p-4 edition-package-diff__header rounded-t-3xl md:p-6">
            <div class="flex flex-col gap-1">
                <p class="text-2xl font-semibold text-black">{{ t('packageDiff.overlimit.header.title') }}</p>
                <p class="text-[12px] text-black">{{ t('packageDiff.overlimit.header.description', {version: customConfig?.versionLabel ?? t(`packages.${packageType}`)}) }}</p>
            </div>
            <div class="items-center hidden md:flex">
                <PtIcon class="shrink-0 !w-[158px] !h-[76px]" :icon="AmountDown"/>
            </div>
            <button class="absolute th-btn th-btn-plain th-btn-only-icon right-4 top-4" @click="close">
                <PtIcon class="text-2xl th-btn-icon" icon="SolidCloseBig"/>
            </button>
        </div>
        <div class="bg-white flex flex-col gap-6 rounded-b-3xl p-4 md:px-[60px] md:py-6">
            <div class="flex flex-col justify-center md:flex-row">
                <PackageCard
                    class="order-1 md:order-2"
                    :type="EditionType.OVERLIMIT_RECOMMAND"
                    :packageType="packageType"
                    :version="version"
                    :highlight="true"
                    :features="customConfig?.features"
                    @choose="handleChoose"
                    @close="close"
                >
                    <template #button>
                        <span class="absolute bottom-[32px] right-0 text-sm text-[#020a1a] bg-[#ffd02f] rounded-[16px_16px_16px_0] px-3 py-[6px] font-[600]">
                            {{ t('packageDiff.overlimit.recommend') }}
                        </span>
                    </template>
                </PackageCard>
            </div>
        </div>
    </div>
    <div v-else-if="usageType === UpgradeUsageType.TRIAL_END" class="relative package-diff-edition trial-end">
        <button class="absolute th-btn th-btn-plain th-btn-only-icon right-4 top-4" @click="close">
            <PtIcon class="text-2xl th-btn-icon" icon="SolidCloseBig"/>
        </button>
        <div class="flex flex-col items-center p-4 bg-white rounded-3xl md:px-12 md:py-10">
            <PtIcon class="shrink-0 !w-[190px] !h-[114px] mb-10" :icon="AmountDown"/>
            <h2 class="text-2xl font-semibold text-black mb-4 max-w-[520px] text-center">
                <span v-html="t(`packageDiff.trialEnd.title`, { packages: `<span>${expiredPackages}</span>` })"></span>
            </h2>
            <p class="mb-10 text-sm text-black">
                {{ t('packageDiff.trialEnd.upgrade') }}
            </p>

            <button class="th-btn th-btn-lg mb-4 w-[300px]" @click="toUpgradePage">
                {{ t('packageDiff.trialEnd.upgradeButton') }}
            </button>
            <button class="th-btn th-btn-text text-gray-90 hover:text-gray-60" @click="close">
                {{ t('packageDiff.trialEnd.continueButton') }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import _ from 'lodash';
import { useI18n } from 'vue-i18n';
import { PtIcon } from 'patsnap-biz';
import PackageCard from './EditionPackageCard.vue';
import { UsageAmountWhere, BiTrackPoint, UpgradeUsageType, EditionType, AIR_UPGRADE_EVENT_TYPE } from '../../constants';
import AmountDown from '../../assets/icon/amount-down.svg';
import { useEditionControl } from '../../composables/use-edition-control';
import { cn, en, jp, tw } from '../../infrastructure/locales';
import { useBiTrack } from '../../../../composables/use-bi-track';
import { openClueReport } from '@eureka/clue-report';
import type { EurekaPackageType } from '@eureka/services/src/apis/common.api.types';
import '@patsnap-ui/icon/assets/solid/CloseBig.svg'
import { computed } from 'vue';
import commonProductPackageMessage from '@eureka/language-tool/locales/common-product/package'

const { t } = useI18n({
    messages: _.merge(
        {
            cn, en, jp, tw,
        },
        commonProductPackageMessage
    ),
});

const { editionControl } = useEditionControl();
const { biTrack } = useBiTrack(AIR_UPGRADE_EVENT_TYPE);
const SHOWN_PACKAGE_COUNT = 3;

const expiredPackages = computed(() => {
    const pkgs = editionControl.value?.expired_packages || [];
    const pkgNames = pkgs.map(pkg => t(`commonProductPackage.${pkg}`));
    const pkgStr = _.take(pkgNames, SHOWN_PACKAGE_COUNT).join('、');
    return `<span class="text-primary">${pkgStr}${pkgs.length > SHOWN_PACKAGE_COUNT ? '...' : ''}</span>`
})

const props = defineProps<{
    usageType: UpgradeUsageType,
    useWhere: UsageAmountWhere,
    packageType: EurekaPackageType,
    customConfig?: {
        versionLabel?: string;
        features?: string[];
    }
}>();

const version = computed(() => {
    return props.packageType.split('_')[0];
});

const emits = defineEmits<{
    (event: 'close'): void
}>();

function close() {
    emits('close');
}

function toUpgradePage() {
    close();

    const lifeSciencesCheckList: UsageAmountWhere[] = [
        UsageAmountWhere.LS_CHAT_AGENT,
        UsageAmountWhere.LS_DOC_AGENT,
        UsageAmountWhere.LS_MARKUSH_DRAFT_AGENT
    ]

    if (lifeSciencesCheckList.includes(props.useWhere)) {
        location.href = '/user-setting/package?recommend_product=ls';
    } else {
        location.href = '/user-setting/package';
    }
}

function handleChoose(isRecommend: boolean) {
    if (isRecommend) {
        biTrack({
            sourceType: props.usageType === UpgradeUsageType.TRIAL_END ? BiTrackPoint.AIR_TRIALEND_UPGRADE_CLICK : BiTrackPoint.AIR_USAGE_UPGRADE_CLICK,
            eventDetailStr: {
                use_where: props.useWhere,
            },
        });
    }

    openClueReport(props.packageType);
}
</script>

<style lang="scss">
.edition-package-diff__header  {
    background: linear-gradient(92.07deg, #DDF8ED 0%, #EDFCFF 30.33%, #DDF0F8 52.41%, #B9D9FF 99.82%);
}
</style>
